/**
 * Cosmos Homepage v2 — single-file collection of new homepage components.
 *
 * Structure (top → bottom):
 *   1. NewNavBar           — top-fixed, config-driven nav
 *   2. HeroSection         — badge, headline, dynamic stats
 *   3. SearchSection       — keyword search (Typesense) + bubbles
 *   4. SearchResults       — locked filter chips + teaser cards
 *   5. UniversityTicker    — horizontal infinite marquee
 *   6. TrustedBy           — "Trusted by R&D leaders at" logo cards
 *      SuccessStories      — matchmaking pairs (was Cosmic Connections);
 *                            both admin-edited (Admin → Homepage)
 *   7. RoadmapSection      — 3 product pillars
 *   8. Testimonials        — review cards
 *   9. DiscoverNext        — rotating word + CTA + List Your IP modal
 *  10. NewFooter           — newsletter + link columns
 *
 * Built per Cosmos_Homepage_Dev_Spec.pdf v1.0. Logged-out flow only — the
 * existing post-login app handles authenticated users.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SubmarkLogo } from './Icons';
import { trackClick } from '../../utils/analytics';
import { apiService } from '../../services/apiService';
import type { HomeSearchSource, HomepageContent, HomepageTrustedItem, HomepageStoryRow } from '../../services/apiService';

// ---------------------------------------------------------------------------
// 1. Nav bar
// ---------------------------------------------------------------------------

export interface NavItem {
  id: string;
  label: string;
  href: string;          // internal route or external URL
  external?: boolean;
  onClick?: () => void;  // overrides href when present
  active?: boolean;      // the page this nav is on (bold + underline, aria-current)
}

interface NewNavBarProps {
  navItems: NavItem[];
  onLoginClick: () => void;
  onLogoClick?: () => void;
  /** Extra control before Sign Up / Log In, on desktop and mobile — e.g. the
   *  IP Intel Report page's "Order a report". */
  rightAction?: React.ReactNode;
}

/** Site-wide entry for the IP Intel Report page (/ip-intel-report, added
 *  2026-09-27). Appended to every page's nav by NewNavBar itself, so no page
 *  has to remember it; a page that lists it (the report page, marked active)
 *  keeps its own. The ipintel_* click lets the admin IP Intel tab count
 *  arrivals from the nav alongside the page's own events. */
const IP_INTEL_NAV_ITEM: NavItem = {
  id: 'ip_intel_report',
  label: 'IP Intel Report',
  href: '/ip-intel-report',
  onClick: () => {
    trackClick('ipintel_click_nav_link', { where: 'nav', from: window.location.pathname });
    window.location.href = '/ip-intel-report';
  },
};

export const NewNavBar: React.FC<NewNavBarProps> = ({ navItems: pageItems, onLoginClick, onLogoClick, rightAction }) => {
  const navItems = pageItems.some(i => i.id === IP_INTEL_NAV_ITEM.id) ? pageItems : [...pageItems, IP_INTEL_NAV_ITEM];
  return (
    <header className="sticky top-0 z-40 w-full bg-white/90 backdrop-blur-md border-b border-grayscale-200">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        {/* Logo */}
        <button
          onClick={() => { trackClick('nav_logo'); onLogoClick?.(); }}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          aria-label="Go to homepage"
        >
          <SubmarkLogo />
        </button>

        {/* Desktop nav — full nav links + Login button */}
        <nav className="hidden md:flex items-center gap-6 lg:gap-8">
          {navItems.map(item => (
            <NavLink key={item.id} item={item} />
          ))}
          {rightAction}
          <button
            onClick={() => { trackClick('nav_login_signup'); onLoginClick(); }}
            className="px-5 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm transition-colors shadow-sm"
          >Sign Up / Log In</button>
        </nav>

        {/* Mobile — Sign Up / Log In button (replaces the old hamburger drawer).
            Per product, mobile no longer needs a secondary nav drawer; About Us
            and other nav-item destinations are reachable from the footer on
            small screens, so we surface the primary CTA directly. */}
        <div className="md:hidden flex items-center gap-2">
          {rightAction}
          <button
            onClick={() => { trackClick('nav_login_signup_mobile'); onLoginClick(); }}
            className="px-3.5 py-1.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-medium text-xs sm:text-sm transition-colors shadow-sm whitespace-nowrap"
          >Sign Up / Log In</button>
        </div>
      </div>
    </header>
  );
};

const NavLink: React.FC<{ item: NavItem; mobile?: boolean; onClickAfter?: () => void }> = ({ item, mobile, onClickAfter }) => {
  const handle = (e: React.MouseEvent) => {
    trackClick(`nav_${item.id}`);
    if (item.onClick) {
      e.preventDefault();
      item.onClick();
    }
    onClickAfter?.();
  };
  const className = mobile
    ? 'block px-2 py-2.5 text-grayscale-1300 font-medium hover:bg-grayscale-50 rounded'
    : item.active
      // The IP Intel design's active state: bold, darker tan, 2px underline.
      ? 'text-[#725737] font-bold text-sm border-b-2 border-[#886A44] pb-1 -mb-[6px]'
      : 'text-grayscale-1200 hover:text-grayscale-1300 font-medium text-sm transition-colors';
  const current = item.active ? { 'aria-current': 'page' as const } : {};
  if (item.external) {
    return <a href={item.href} target="_blank" rel="noopener noreferrer" onClick={handle} className={className} {...current}>{item.label}</a>;
  }
  return <a href={item.href} onClick={handle} className={className} {...current}>{item.label}</a>;
};


// ---------------------------------------------------------------------------
// 2. Hero section — badge + headline + dynamic stats
// ---------------------------------------------------------------------------

/** /api/v1/homepage/stats → stats. Since 2026-10 each `*_new_this_month` is
 *  the "+X NEW!" count over a rolling window (last 30 days by default) or a
 *  number the admin typed (Admin → Homepage → Hero numbers), so the line no
 *  longer vanishes on the 1st of every month. `*_show_new` is false when the
 *  admin switched a metric's line off; an older API sends neither it nor the
 *  label, which means "show it". */
export interface HeroStats {
  universities: number;
  universities_new_this_month: number;
  universities_show_new?: boolean;
  universities_new_label?: string;
  tech_offers: number;
  tech_offers_new_this_month: number;
  tech_offers_show_new?: boolean;
  tech_offers_new_label?: string;
  countries: number;
  countries_new_this_month: number;
  countries_show_new?: boolean;
  countries_new_label?: string;
}

interface HeroSectionProps {
  stats: HeroStats | null;
  loading: boolean;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ stats, loading }) => (
  <section className="w-full pt-4 sm:pt-6 lg:pt-8 pb-6 sm:pb-10 text-center">
    {/* Badge — uses the canonical blue button spec
        (bg #dce7f1, border #5085c4, text #13325b) and bold weight per spec. */}
    <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-secondary-100 border border-secondary-400 text-secondary-og-600 text-xs sm:text-sm font-bold tracking-wide">
      UNIVERSITY IP · INDUSTRY GROWTH
    </div>

    {/* Headline — short "tech" word keeps the heading on two lines even
        on phones (previously "technology" wrapped onto a third line). */}
    <h1 className="mt-5 sm:mt-7 text-3xl sm:text-5xl lg:text-6xl font-bold text-grayscale-1300 leading-tight px-3">
      Licence <span className="text-secondary-og-600">tech</span> that gives
      <br className="hidden sm:block" />
      <span className="sm:hidden"> </span>your business a real edge
    </h1>

    {/* Stats */}
    <div className="mt-8 sm:mt-12 grid grid-cols-3 gap-3 sm:gap-8 max-w-3xl mx-auto px-3">
      <StatBlock label="Universities & Labs" value={stats?.universities} delta={stats?.universities_new_this_month} showNew={stats?.universities_show_new} loading={loading} />
      <StatBlock label="Tech Offers" value={stats?.tech_offers} delta={stats?.tech_offers_new_this_month} showNew={stats?.tech_offers_show_new} loading={loading} />
      <StatBlock label="Countries" value={stats?.countries} delta={stats?.countries_new_this_month} showNew={stats?.countries_show_new} loading={loading} />
    </div>
  </section>
);

const StatBlock: React.FC<{ label: string; value?: number; delta?: number; showNew?: boolean; loading?: boolean }> = ({ label, value, delta, showNew, loading }) => (
  <div className="flex flex-col items-center min-w-0 px-1">
    {/* Stack on every breakpoint to avoid mobile overflow ("+1545 NEW!" is
        too long to sit beside "26,540" in a 33%-wide column). The desktop
        version still looks balanced because the column is wide enough.
        Generous vertical spacing keeps the +N tag from kissing the label. */}
    <div className="flex flex-col items-center gap-1.5 sm:gap-2 max-w-full">
      <span className="text-2xl sm:text-5xl font-bold text-grayscale-1300 leading-none">
        {loading ? '—' : (value ?? 0).toLocaleString()}
      </span>
      {/* Hidden while loading, when the admin switched the line off, and at
          0 (FALLBACK_STATS carries 0s, so a failed fetch never advertises
          growth we haven't verified). */}
      {!loading && showNew !== false && delta !== undefined && delta > 0 && (
        <span className="text-[10px] sm:text-xs font-semibold text-secondary-og-600 whitespace-nowrap leading-none">
          +{delta.toLocaleString()} NEW!
        </span>
      )}
    </div>
    <span className="mt-3 sm:mt-3 text-[11px] sm:text-sm text-grayscale-1100 text-center leading-tight">{label}</span>
  </div>
);


// ---------------------------------------------------------------------------
// 3. Search section — keyword input + bubbles + Surprise Me
// ---------------------------------------------------------------------------

// Visible "quick-pick" bubbles below the search input (3 only — keeping the
// row narrow on mobile). The "Surprise Me" button picks randomly from the
// larger SURPRISE_POOL so users can discover keywords beyond the 3 displayed.
// Admin → Homepage → Search chips edits both (homepage_content `search`);
// these are the built-in copy, used until that answer arrives.
const DEFAULT_BUBBLE_KEYWORDS = [
  'Battery storage IP',
  'Drug delivery patents',
  'Computer vision tech',
];

const SURPRISE_POOL = [
  'Battery storage IP',
  'Drug delivery patents',
  'Computer vision tech',
  'iot device for farming',
  'india agriculture',
  'sustainable development',
  'wearable technology',
  'education tech',
];

interface SearchSectionProps {
  query: string;
  setQuery: (q: string) => void;
  /** `source` says which control ran it (box / suggestion chip / Surprise Me);
   *  the server logs it with the search. Optional so a plain (q) handler still fits. */
  onSubmit: (q: string, source?: HomeSearchSource) => void;
  /** The chips under the box; missing or empty = the built-in three. */
  bubbles?: string[];
  /** What "Surprise Me" picks from; missing = the built-in pool, [] = no Surprise button. */
  surprisePool?: string[];
  /** The Surprise button's text (default "Surprise Me"). */
  surpriseLabel?: string;
  /** The admin switched the chips off: no chip row at all. */
  hideBubbles?: boolean;
}

export const SearchSection: React.FC<SearchSectionProps> = ({
  query, setQuery, onSubmit, bubbles, surprisePool, surpriseLabel, hideBubbles,
}) => {
  // Visible bubble row uses only the short list; "Surprise Me" picks from the
  // wider pool so users can discover keywords beyond the ones displayed.
  const visibleBubbles = hideBubbles ? [] : (bubbles && bubbles.length > 0 ? bubbles : DEFAULT_BUBBLE_KEYWORDS);
  const pool = hideBubbles ? [] : (Array.isArray(surprisePool) ? surprisePool : SURPRISE_POOL);
  const surpriseText = (surpriseLabel || '').trim() || 'Surprise Me';
  // Fires once per session-of-typing: when the user goes from empty → first
  // character. Avoids flooding PostHog on every keystroke while still giving
  // a "% of visitors who typed something" funnel metric.
  const typingStartedRef = useRef(false);
  const handleSurprise = () => {
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    setQuery(pick);
    trackClick('search_surprise_me', { keyword: pick });
    onSubmit(pick, 'surprise');
  };
  const submit = () => {
    trackClick('search_submit', { query, length: query.length });
    onSubmit(query, 'typed');
  };
  const handleChange = (next: string) => {
    setQuery(next);
    if (next.length > 0 && !typingStartedRef.current) {
      typingStartedRef.current = true;
      trackClick('search_typing_started');
    }
    if (next.length === 0) {
      // Reset so re-typing in the same session also fires the event again.
      typingStartedRef.current = false;
    }
  };
  return (
    <section className="w-full px-3 sm:px-4 max-w-3xl mx-auto">
      <div className="relative">
        <span className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-grayscale-1000">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="2" />
            <path d="M16 16L21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </span>
        <input
          type="text"
          value={query}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => trackClick('search_input_focus')}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          placeholder="Search by tech, field or keyword. Press ENTER."
          className="w-full pl-11 sm:pl-12 pr-12 sm:pr-14 py-3 sm:py-4 rounded-full border border-grayscale-300 bg-white text-grayscale-1300 placeholder-grayscale-1000 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 text-sm sm:text-base"
        />
        {/* Enter / return key indicator — clickable so it also acts as a
            submit button for users who don't realise the input is Enter-driven. */}
        <button
          type="button"
          onClick={submit}
          aria-label="Search"
          className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full text-grayscale-1000 hover:text-grayscale-1300 hover:bg-grayscale-100 flex items-center justify-center transition-colors"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            {/* Standard enter / return key glyph: horizontal top stroke,
                curve down on the right, then arrow-headed line back to
                the left — matches the design reference. */}
            <path d="M20 6V10C20 11.1046 19.1046 12 18 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 9L6 12L9 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      {(visibleBubbles.length > 0 || pool.length > 0) && (
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {visibleBubbles.map(kw => (
          <button
            key={kw}
            onClick={() => { trackClick('search_bubble', { keyword: kw }); setQuery(kw); onSubmit(kw, 'chip'); }}
            className="px-3 sm:px-4 py-1.5 rounded-full bg-white border border-primary-500 text-grayscale-1200 text-xs sm:text-sm hover:bg-primary-50 hover:text-grayscale-1300 transition-colors"
          >{kw}</button>
        ))}
        {pool.length > 0 && (
        <button
          onClick={handleSurprise}
          className="px-3 sm:px-4 py-1.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-xs sm:text-sm font-medium transition-colors"
        >{surpriseText}</button>
        )}
      </div>
      )}
    </section>
  );
};


// ---------------------------------------------------------------------------
// 4. Search results — locked filter chips + teaser cards
// ---------------------------------------------------------------------------

/**
 * Return the first complete sentence from a body of text.
 *
 * The teaser cards on the homepage previously truncated summaries mid-word
 * with `line-clamp-2`, which produced awkward "...obstacle navigation. It
 * features low mechanical complexity, minimal added weight, and..." cutoffs.
 * Per product, we now show only the first sentence in full so each card is
 * self-contained and the user has to sign up to read the rest.
 *
 * "Sentence" = anything up to the first `.`, `!`, or `?` followed by a
 * whitespace or end-of-string. Falls back to the whole string when no
 * terminator is found (very short summaries).
 */
const firstSentence = (text: string): string => {
  if (!text) return '';
  const match = text.match(/^[\s\S]*?[.!?](?=\s|$)/);
  return (match ? match[0] : text).trim();
};

interface SearchResultsProps {
  query: string;
  loading: boolean;
  results: any[];
  total: number;
  onLockedClick: () => void;        // sign-up trigger for any locked control
  onClear?: () => void;             // wipe query + results back to the empty state
  /** The search these cards came from — the server's log row id and the query
   *  it ran (not the live input, which may have been edited since). Attached to
   *  every click below so the admin log can say what was clicked after which search. */
  searchId?: string | null;
  searchedQuery?: string;
}

export const SearchResults: React.FC<SearchResultsProps> = ({ query, loading, results, total, onLockedClick, onClear, searchId, searchedQuery }) => {
  if (!query) return null;
  const sid = searchId ?? null;
  const sq = searchedQuery ?? query;
  // A locked filter still opens sign-up; the event says which one tempted them.
  const lockedFilter = (filter: string) => () => {
    trackClick('search_locked_filter', { filter, search_id: sid });
    onLockedClick();
  };
  return (
    <section className="w-full px-3 sm:px-4 max-w-4xl mx-auto mt-6 sm:mt-8">
      {/* Filter chips — locked */}
      <div className="flex flex-wrap items-center gap-2 mb-4 sm:mb-5">
        <LockedChip label="ascending" />
        <LockedChip label="TRL (12)" tooltip="Sign up to unlock filters" onClick={lockedFilter('TRL')} />
        <LockedChip label="Country (12)" tooltip="Sign up to unlock filters" onClick={lockedFilter('Country')} />
        <LockedChip label="University (12)" tooltip="Sign up to unlock filters" onClick={lockedFilter('University')} />
        <LockedChip label="Industry (12)" tooltip="Sign up to unlock filters" onClick={lockedFilter('Industry')} />
        <button
          type="button"
          onClick={() => { trackClick('search_results_clear', { search_id: sid }); onClear?.(); }}
          className="ml-auto text-xs text-grayscale-1100 hover:text-grayscale-1300 hover:underline transition-colors"
        >clear</button>
      </div>

      <h3 className="text-sm font-semibold text-grayscale-1300 mb-3">Popular Searches</h3>

      {loading && (
        <div className="text-sm text-grayscale-1100">Searching…</div>
      )}

      {!loading && results.length === 0 && (
        <div className="text-sm text-grayscale-1100 p-4 bg-grayscale-50 rounded-lg">
          No results found. Try a different keyword.
        </div>
      )}

      <div className="space-y-3">
        {results.map((r, i) => {
          const m = (r && r.metadata) || {};
          const title = m.utech_title || m.title || 'Untitled technology';
          const summary = m.utech_summary || m.short_summary || '';
          const trl = m.calculated_trl;
          return (
            <article
              key={`${i}-${m.utech_technology_id || 'r'}`}
              className="relative flex flex-col p-4 sm:p-5 bg-primary-100 rounded-2xl border border-primary-200 utech-nocopy"
            >
              <div className="flex items-start justify-between gap-3 mb-1.5">
                <h4 className="font-semibold text-grayscale-1300 text-sm sm:text-base pr-16 sm:pr-20">{title}</h4>
                {trl !== null && trl !== undefined && (
                  <span className="hidden sm:inline-block text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                    TRL {trl}
                  </span>
                )}
              </div>
              <p className="text-xs sm:text-sm text-grayscale-1200 sm:pr-32">
                {firstSentence(summary)}
              </p>
              {/* Mobile: button on its own row below the text, right-aligned.
                  Desktop: floats absolutely bottom-right, with sm:pr-32 above
                  reserving space. This stops the button from overlapping the
                  description on narrow viewports. */}
              <button
                onClick={() => {
                  trackClick('result_card_signup', {
                    search_id: sid, query: sq, rank: i + 1,
                    utech_technology_id: m.utech_technology_id ?? null, title,
                  });
                  onLockedClick();
                }}
                className="mt-3 self-end sm:mt-0 sm:absolute sm:right-4 sm:bottom-4 sm:self-auto px-4 py-1.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-[11px] sm:text-xs font-medium transition-colors whitespace-nowrap"
              >Read More</button>
            </article>
          );
        })}
      </div>

      {!loading && results.length > 0 && (
        <div className="flex justify-center mt-5 sm:mt-7">
          <button
            onClick={() => { trackClick('result_view_more', { search_id: sid, query: sq, total }); onLockedClick(); }}
            className="px-6 sm:px-8 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm transition-colors"
          >View More</button>
        </div>
      )}

      {!loading && total > results.length && (
        <p className="text-center text-[11px] text-grayscale-1000 mt-2">
          Showing {results.length} of {total.toLocaleString()} results.
        </p>
      )}
    </section>
  );
};

const LockedChip: React.FC<{ label: string; tooltip?: string; onClick?: () => void }> = ({ label, tooltip, onClick }) => (
  <button
    onClick={onClick}
    title={tooltip}
    // Per design spec: white fill, medium-blue border (#5085c4), dark-navy
    // text (#13325b). Hover fills with the light-azure tint.
    className={`inline-flex items-center px-3 py-1 rounded-full border text-xs sm:text-sm transition-colors bg-white border-secondary-400 text-secondary-og-600 ${
      onClick ? 'cursor-pointer hover:bg-secondary-100' : ''
    }`}
  >
    {/* Tick (default chip) or padlock (locked chip with sign-up gate). */}
    {tooltip ? (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="mr-1">
        <path d="M6 10V8a6 6 0 1112 0v2" stroke="currentColor" strokeWidth="2" />
        <rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
      </svg>
    ) : (
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="mr-1">
        <path d="M5 12.5L10 17L19 7.5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    )}
    {label}
  </button>
);


// ---------------------------------------------------------------------------
// 5. University ticker — top 10-15 universities marquee
// ---------------------------------------------------------------------------

export interface UniversityTickerItem {
  name: string;       // short name, e.g. "MIT"
  country: string;    // full country name, used for flag mapping
  flag?: string;      // emoji override (otherwise derived from country)
  count: number;      // tech offers count
  delta?: number;     // new tech offers since previous snapshot
}

// Map country name → ISO-3166-1 alpha-2 code (lowercase) used by
// `flag-icons` (https://github.com/lipis/flag-icons) on jsdelivr.
//
// We render flags as fixed 4:3 SVG images from this CDN instead of native
// emoji (whose per-platform aspect ratios make rows look jagged — Indian
// flag is 2:3, US is 10:19, Australian is 1:2, etc.) so every card shows
// a flag of the same dimensions and crispness on every device.
export const COUNTRY_ISO: Record<string, string> = {
  'United States': 'us',
  'India': 'in',
  'United Kingdom': 'gb',
  'Germany': 'de',
  'Australia': 'au',
  'Canada': 'ca',
  'France': 'fr',
  'Japan': 'jp',
  'South Korea': 'kr',
  'China': 'cn',
  'Italy': 'it',
  'Spain': 'es',
  'Netherlands': 'nl',
  'Singapore': 'sg',
  'Switzerland': 'ch',
  'Ireland': 'ie',
  'Sweden': 'se',
  'Israel': 'il',
  'Brazil': 'br',
};

export const flagSrc = (country: string): string => {
  const iso = COUNTRY_ISO[country] || 'un';
  return `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.2.3/flags/4x3/${iso}.svg`;
};

interface UniversityTickerProps {
  items: UniversityTickerItem[];
  /**
   * Optional handler for a "View all universities" CTA rendered under the
   * marquee. When omitted the button is hidden — pass it on the homepage so
   * users can jump to the full directory at /universities.
   */
  onViewAll?: () => void;
}

export const UniversityTicker: React.FC<UniversityTickerProps> = ({ items, onViewAll }) => {
  if (items.length === 0) return null;
  // Duplicate the list once so the CSS marquee can loop seamlessly with translateX(-50%).
  const doubled = [...items, ...items];
  return (
    <section className="w-full py-10 sm:py-14">
      {/* Constrain the whole section to the same content width as the rest
          of the page (max-w-screen-xl) and clip overflow so the marquee can
          only scroll *inside* the working area — anything that runs past the
          left/right edges of the box is hidden, not just faded.

          The fade gradient on top still gives the soft cutoff per the spec. */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="text-center mb-6 sm:mb-8 px-3">
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-grayscale-1300">
            Tech offers from the world's leading research institutions
          </h2>
          <p className="mt-2 text-sm sm:text-base text-grayscale-1100">
            Summarised &amp; contextualised on a single platform for seamless matchmaking
          </p>
        </div>
        <div className="relative cosmos-marquee-mask">
          <div className="flex gap-3 sm:gap-4 cosmos-marquee" style={{ width: 'max-content' }}>
            {doubled.map((u, i) => (
              <div
                key={`${u.name}-${i}`}
                className="flex flex-col items-center w-[110px] sm:w-auto sm:min-w-[160px] px-2 py-3 sm:px-5 sm:py-4 rounded-2xl border border-grayscale-200 bg-white shrink-0"
              >
                {/* Flag SVG from the flag-icons CDN — fixed 4:3 ratio so
                    every card lines up regardless of country. */}
                <img
                  src={flagSrc(u.country)}
                  alt={u.country}
                  width={32}
                  height={24}
                  loading="lazy"
                  className="w-8 h-6 sm:w-9 sm:h-[27px] object-cover rounded-sm shadow-sm border border-grayscale-100"
                />
                <span className="mt-2 text-sm sm:text-base font-semibold text-grayscale-1300 truncate max-w-[140px]">
                  {u.name}
                </span>
                {u.count > 0 && (
                  <span className="mt-0.5 text-[10px] sm:text-xs text-grayscale-1100">
                    {u.count.toLocaleString()} technologies
                  </span>
                )}
                {u.delta !== undefined && u.delta > 0 && (
                  <span className="mt-0.5 text-[9px] sm:text-[10px] font-semibold text-secondary-og-600 whitespace-nowrap">
                    +{u.delta.toLocaleString()} NEW
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* "View all universities" CTA — sits directly below the carousel
            so users browsing the marquee can jump to the full
            /universities directory in a single tap. Optional handler so
            this stays hidden when the parent page doesn't wire it (e.g. an
            embedded preview). */}
        {onViewAll && (
          <div className="mt-6 sm:mt-8 text-center">
            <button
              onClick={() => { trackClick('university_ticker_view_all'); onViewAll(); }}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full border border-primary-600 bg-white hover:bg-primary-100 text-primary-700 font-semibold text-sm transition-colors"
            >
              <span>View all universities</span>
              <span aria-hidden="true">→</span>
            </button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes cosmosMarquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .cosmos-marquee {
          /* Slow scroll — 90s/cycle keeps cards readable without holding the
             user's attention. Hover pauses so they can park on a card. */
          animation: cosmosMarquee 90s linear infinite;
        }
        .cosmos-marquee:hover {
          animation-play-state: paused;
        }
        .cosmos-marquee-mask {
          -webkit-mask-image: linear-gradient(to right, transparent 0, #000 80px, #000 calc(100% - 80px), transparent 100%);
                  mask-image: linear-gradient(to right, transparent 0, #000 80px, #000 calc(100% - 80px), transparent 100%);
        }
        @media (max-width: 640px) {
          .cosmos-marquee-mask {
            -webkit-mask-image: linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%);
                    mask-image: linear-gradient(to right, transparent 0, #000 32px, #000 calc(100% - 32px), transparent 100%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .cosmos-marquee { animation: none; }
        }
      `}</style>
    </section>
  );
};


// ---------------------------------------------------------------------------
// 6. Trusted by + Success Stories — admin-edited (Admin → Homepage)
// ---------------------------------------------------------------------------
//
// Both come from GET /api/v1/homepage/content, which App.tsx fetches once and
// hands down in halves. Until it answers — or when it cannot — they show the
// built-in copy below, which mirrors the server's defaults
// (Backend/homepage_content.py), so a failed fetch looks like a fresh install
// rather than a hole in the page.

const DEFAULT_TRUSTED: HomepageContent['trusted'] & { subtitle: string } = {
  enabled: true,
  // A grid of logo cards under a title and subtitle (owner, 2026-10-14);
  // the two-row and scrolling banners stay as admin choices.
  title: 'Trusted by R&D leaders at',
  subtitle: 'Industry, investors and research institutions already discovering tech on Cosmos',
  layout: 'cards',
  more_text: '...more!',
  items: [
    { id: 'lnt', name: 'L&T', logo: '/homepage/logos/lnt-blue.svg' },
    { id: 'perfint', name: 'Perfint Healthcare', logo: '/homepage/logos/perfint.png' },
    { id: 'technica', name: 'Technica Fiber Tech', logo: '/homepage/logos/technica-fiber-tech.jpg' },
    // TIGS's wordmark (owner, 2026-10-11), from tigs.res.in, margins trimmed.
    { id: 'tigs', name: 'Tata Institute for Genetics and Society', logo: '/homepage/logos/tigs-wordmark.png' },
    // TriSail's own site shows its name in plain type, so there is no logo
    // file: the name is set as text in the same slot.
    { id: 'trisail', name: 'TriSail Medical', logo: '' },
    { id: 'vit', name: 'VIT Vellore', logo: '/homepage/logos/vit-vellore.png' },
    { id: 'iitb', name: 'IIT Bombay', logo: '/homepage/logos/iit-bombay.png' },
    { id: 'chrysalix', name: 'Chrysalix VC', logo: '/homepage/logos/chrysalix.svg' },
  ],
};

const DEFAULT_STORIES: HomepageContent['stories'] = {
  enabled: true,
  title: 'Success Stories',
  subtitle: 'High velocity discovery and matchmaking in action',
  rows: [
    // The two newest lead (owner, 2026-10-10 — Canva slide 4); the other six
    // are the original Cosmic Connections rows.
    { from: 'Defence Fiber Optics Supplier', verb: 'Co-Development', to: 'Right researcher matched' },
    { from: 'Mid Sized Medtech Startup', verb: 'Cross Border Licensing', to: '17 Technologies Shortlisted' },
    { from: '$30bn Engineering Group', verb: 'Open Innovation', to: '6 Universities Shortlisted' },
    { from: 'Nutraceuticals Market Leader', verb: 'Cross Border Licensing', to: 'Knee Pain Relief IP Matched' },
    { from: 'Listed SME in Digital Health', verb: 'Co-Development', to: '3 Prototype Innovation Partners' },
    { from: 'Mid-Sized Textile Manufacturer', verb: 'Costs Optimisation', to: '70% Lower Wastewater Treatment' },
    { from: 'Patient Monitoring Startup', verb: 'Product Diversification', to: 'Lead Academic Identified' },
    { from: 'Sports Performance Startup', verb: 'Co-Development', to: 'Centre of Excellence Matched' },
  ],
};

/** A logo's `logo` value → an <img> src, or '' for "show the name as text".
 *  An uploaded logo is served by the API, which is a different host in
 *  production, so it gets the base URL every API call uses; a built-in one is
 *  a file on this site; anything else the server allows is a full https URL. */
const logoSrc = (logo: string): string => {
  const v = (logo || '').trim();
  if (!v) return '';
  if (v.startsWith('/api/')) return `${apiService.getApiBaseUrl()}${v}`;
  if ((v.startsWith('/') && !v.startsWith('//')) || /^https:\/\//i.test(v)) return v;
  return '';
};

/** One logo in a fixed-height slot, in its own colours: every logo the same
 *  height (a small file is scaled up to it) unless that would make it wider
 *  than the slot, and never stretched. No image — or one
 *  that fails to load — shows the name instead. Callers key it by the logo
 *  value, so a changed logo gets a fresh try after a failed one. */
const TrustedLogo: React.FC<{ item: HomepageTrustedItem }> = ({ item }) => {
  const src = logoSrc(item.logo);
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex items-center justify-center h-12 sm:h-14 w-full min-w-0" title={item.name}>
      {src && !failed ? (
        <img
          src={src}
          alt={item.name}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="h-full w-auto max-w-full object-contain"
        />
      ) : (
        <span className="text-sm sm:text-lg font-bold tracking-tight text-grayscale-1200 text-center leading-tight line-clamp-2">
          {item.name}
        </span>
      )}
    </div>
  );
};

/** A logo's height in its card, as a share of the card's logo height
 *  (--trusted-logo-h): a crest or other square-ish logo gets all of it, a
 *  wordmark less — a little more area than a crest, as wordmarks carry less
 *  ink — so a wide wordmark does not dwarf the crests beside it (L&T or IIT
 *  Bombay next to the TIGS wordmark) nor shrink to a thin line. An image with
 *  no size of its own (some SVGs) gets the full height and object-contain
 *  keeps it inside the card. */
const logoScale = (w: number, h: number): number => {
  if (!w || !h) return 1;
  return Math.max(0.5, Math.min(1, Math.pow(1.25 / (w / h), 0.4)));
};

/** One card of the "cards" layout: the logo centred in its own colours, never
 *  stretched, sized by its shape (logoScale) once it has loaded — until then
 *  it fits a box of the logo height by 2.4 times that, which is close. The
 *  card's height is fixed, so the page never moves. No image, or one that
 *  fails to load, shows the name as bold text. Callers key it by the logo
 *  value, so a changed logo starts afresh. */
const TrustedCard: React.FC<{ item: HomepageTrustedItem }> = ({ item }) => {
  const src = logoSrc(item.logo);
  const [failed, setFailed] = useState(false);
  const [scale, setScale] = useState<number | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  // An image the browser already had can finish before onLoad is wired up.
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalWidth) setScale(logoScale(img.naturalWidth, img.naturalHeight));
  }, [src]);
  return (
    <div className="cosmos-trusted-card" title={item.name}>
      {src && !failed ? (
        <img
          ref={imgRef}
          src={src}
          alt={item.name}
          loading="lazy"
          decoding="async"
          onLoad={e => setScale(logoScale(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight))}
          onError={() => setFailed(true)}
          className="cosmos-trusted-card-logo"
          style={scale === null
            ? { maxHeight: 'var(--trusted-logo-h)', maxWidth: 'min(100%, calc(var(--trusted-logo-h) * 2.4))' }
            : { height: `calc(var(--trusted-logo-h) * ${scale.toFixed(3)})`, maxWidth: '100%' }}
        />
      ) : (
        <span className="text-[15px] sm:text-lg lg:text-xl font-bold tracking-tight text-grayscale-1300 text-center leading-tight line-clamp-2">
          {item.name}
        </span>
      )}
    </div>
  );
};

interface TrustedByProps {
  /** `content.trusted` from /api/v1/homepage/content; null → the built-in cards. */
  content?: HomepageContent['trusted'] | null;
  /** Admin → Homepage's preview: no top padding of its own. */
  bare?: boolean;
}

export const TrustedBy: React.FC<TrustedByProps> = ({ content, bare }) => {
  const c = content || DEFAULT_TRUSTED;
  if (c.enabled === false) return null;
  // The server's list is authoritative once it answers (only enabled logos,
  // in order); the built-in one stands in only for a missing list.
  const items = (Array.isArray(c.items) ? c.items : DEFAULT_TRUSTED.items)
    .filter(it => it && typeof it.name === 'string' && it.name.trim());
  if (items.length === 0) return null;
  const title = (c.title || '').trim() || DEFAULT_TRUSTED.title;
  // '' is the admin hiding it; missing is an API from before subtitles.
  const subtitle = typeof c.subtitle === 'string' ? c.subtitle.trim() : DEFAULT_TRUSTED.subtitle;
  const more = typeof c.more_text === 'string' ? c.more_text.trim() : DEFAULT_TRUSTED.more_text;
  const top = bare ? '' : 'pt-10 sm:pt-14';
  // A copy from before 2026-10-14 — the one a returning visitor's browser
  // cached, or an older API — has no subtitle, and its "rows" was only ever
  // the old default (nothing was saved in production), so it gets the cards
  // too rather than the old banner flashing up until the fresh copy answers.
  const layout = c.layout === 'scroll' ? 'scroll'
    : c.layout === 'rows' && typeof c.subtitle === 'string' ? 'rows' : 'cards';

  if (layout === 'cards') {
    // Cards (owner, 2026-10-14): a centred title and subtitle, then equal
    // white cards — four a row on desktop, three on a tablet, two on a phone,
    // a short last row centred. No "...more!" here. No bottom padding of its
    // own — Success Stories' top padding is the gap beneath it.
    return (
      <section className={`w-full ${top} px-4`}>
        <div className="max-w-3xl mx-auto text-center mb-8 sm:mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-grayscale-1300 leading-tight">{title}</h2>
          {subtitle && <p className="mt-2 sm:mt-3 text-base sm:text-lg text-grayscale-900">{subtitle}</p>}
        </div>
        <ul className="cosmos-trusted-grid" role="list">
          {items.map((it, i) => (
            <li key={`${i}-${it.id}-${it.logo}`} className="cosmos-trusted-cell">
              <TrustedCard item={it} />
            </li>
          ))}
        </ul>
        <style>{`
          .cosmos-trusted-grid {
            display: flex; flex-wrap: wrap; justify-content: center; gap: 16px;
            max-width: 1040px; margin: 0 auto; padding: 0; list-style: none;
          }
          .cosmos-trusted-cell { flex: 0 0 calc((100% - 16px) / 2); min-width: 0; }
          .cosmos-trusted-card {
            --trusted-logo-h: 44px;
            display: flex; align-items: center; justify-content: center;
            height: 88px; padding: 12px 16px; overflow: hidden;
            background: #FFFFFF; border: 1px solid #E9E2D8; border-radius: 16px;
          }
          .cosmos-trusted-card-logo { display: block; width: auto; max-width: 100%; object-fit: contain; }
          @media (min-width: 640px) {
            .cosmos-trusted-grid { gap: 20px; }
            .cosmos-trusted-cell { flex-basis: calc((100% - 40px) / 3); }
            .cosmos-trusted-card { --trusted-logo-h: 52px; height: 100px; padding: 16px 20px; }
          }
          @media (min-width: 1024px) {
            .cosmos-trusted-cell { flex-basis: calc((100% - 60px) / 4); }
            .cosmos-trusted-card { --trusted-logo-h: 64px; height: 112px; padding: 20px 24px; }
          }
        `}</style>
      </section>
    );
  }
  const moreCls = 'text-sm sm:text-base italic text-grayscale-900';

  // Two rows on desktop (owner: "8 logos, 2 rows ... ending with ...more!"):
  // the logos split in half — the first row takes an odd one, at most 6 a
  // row — in fixed-width slots, and "...more!" ends the last row instead of
  // wrapping onto a third line. Phones: two per row, "...more!" closing on
  // its own line.
  const perRow = Math.min(6, Math.max(1, Math.ceil(items.length / 2)));
  const desktopRows: HomepageTrustedItem[][] = [];
  for (let i = 0; i < items.length; i += perRow) desktopRows.push(items.slice(i, i + perRow));
  const rows = (
    <>
      <div className="sm:hidden flex flex-wrap justify-center items-center -my-2">
        {items.map((it, i) => (
          <div key={`${i}-${it.id}-${it.logo}`} className="w-1/2 px-3 py-2">
            <TrustedLogo item={it} />
          </div>
        ))}
        {more && <div className={`w-full pt-1 text-center ${moreCls}`}>{more}</div>}
      </div>
      <div className="hidden sm:flex flex-col items-center gap-4 lg:gap-6">
        {desktopRows.map((row, r) => (
          <div key={r} className="flex justify-center items-center">
            {row.map((it, i) => (
              <div key={`${i}-${it.id}-${it.logo}`} className="w-28 md:w-36 lg:w-44 px-3 lg:px-4">
                <TrustedLogo item={it} />
              </div>
            ))}
            {more && r === desktopRows.length - 1 && (
              <div className={`px-3 lg:px-5 whitespace-nowrap ${moreCls}`}>{more}</div>
            )}
          </div>
        ))}
      </div>
    </>
  );

  let body: React.ReactNode = rows;
  if (layout === 'scroll') {
    // One lap = every logo then "...more!". A short list repeats the lap
    // within each copy so a copy is always wider than the banner and the
    // loop never shows a gap. Two identical copies + translateX(-50%) make
    // the seamless wrap (as UniversityTicker does); the slots carry their
    // own spacing instead of a flex gap so the halves measure exactly equal.
    const lap = [
      ...items.map(item => ({ item })),
      ...(more ? [{ item: null as HomepageTrustedItem | null }] : []),
    ];
    const reps = Math.max(1, Math.ceil(10 / items.length));
    const copy = Array.from({ length: reps }, () => lap).flat();
    const seconds = Math.max(20, copy.length * 3);
    body = (
      <>
        <div className="cosmos-trusted-scroll relative overflow-hidden cosmos-trusted-mask">
          <div className="flex items-center cosmos-trusted-marquee" style={{ width: 'max-content', animationDuration: `${seconds}s` }}>
            {[0, 1].map(half => (
              // The second copy only exists for the wrap — hidden from
              // screen readers so the names are not read out twice.
              <div key={half} className="flex items-center shrink-0" aria-hidden={half === 1 ? true : undefined}>
                {copy.map((e, i) => e.item ? (
                  <div key={`${i}-${e.item.id}-${e.item.logo}`} className="shrink-0 w-32 sm:w-44 px-4 sm:px-6">
                    <TrustedLogo item={e.item} />
                  </div>
                ) : (
                  <div key={`more-${i}`} className={`shrink-0 px-4 sm:px-6 whitespace-nowrap ${moreCls}`}>{more}</div>
                ))}
              </div>
            ))}
          </div>
        </div>
        {/* Visitors who asked their device for less motion get the rows. */}
        <div className="cosmos-trusted-static">{rows}</div>
        <style>{`
          @keyframes cosmosTrustedMarquee {
            0%   { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .cosmos-trusted-marquee {
            animation: cosmosTrustedMarquee 30s linear infinite;
          }
          .cosmos-trusted-marquee:hover {
            animation-play-state: paused;
          }
          .cosmos-trusted-mask {
            -webkit-mask-image: linear-gradient(to right, transparent 0, #000 64px, #000 calc(100% - 64px), transparent 100%);
                    mask-image: linear-gradient(to right, transparent 0, #000 64px, #000 calc(100% - 64px), transparent 100%);
          }
          @media (max-width: 640px) {
            .cosmos-trusted-mask {
              -webkit-mask-image: linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
                      mask-image: linear-gradient(to right, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
            }
          }
          .cosmos-trusted-static { display: none; }
          @media (prefers-reduced-motion: reduce) {
            .cosmos-trusted-scroll { display: none; }
            .cosmos-trusted-static { display: block; }
          }
        `}</style>
      </>
    );
  }

  // A banner rather than a section: one card in the testimonial cards'
  // style (white keeps the logos' own white backgrounds invisible), a title
  // one step below the section headings, and no bottom padding of its own —
  // Success Stories' top padding is the gap beneath it.
  return (
    <section className={`w-full ${top} px-3 sm:px-4`}>
      <div className="max-w-4xl mx-auto rounded-2xl border border-primary-200 bg-white shadow-sm px-4 py-6 sm:px-8 sm:py-8">
        <div className="text-center mb-5 sm:mb-7">
          <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-grayscale-1300">{title}</h2>
          {subtitle && <p className="mt-1.5 text-sm sm:text-base text-grayscale-900">{subtitle}</p>}
        </div>
        {body}
      </div>
    </section>
  );
};

interface SuccessStoriesProps {
  /** `content.stories` from /api/v1/homepage/content; null → the built-in rows. */
  content?: HomepageContent['stories'] | null;
}

export const SuccessStories: React.FC<SuccessStoriesProps> = ({ content }) => {
  const c = content || DEFAULT_STORIES;
  if (c.enabled === false) return null;
  const rows = (Array.isArray(c.rows) ? c.rows : DEFAULT_STORIES.rows)
    .filter((r): r is HomepageStoryRow => !!r && !!r.from && !!r.verb && !!r.to);
  // Every row deleted in the admin: no heading over an empty space.
  if (rows.length === 0) return null;
  const title = (c.title || '').trim() || DEFAULT_STORIES.title;
  // The first word takes the accent colour, as "Cosmic" did.
  const cut = title.indexOf(' ');
  const accent = cut > 0 ? title.slice(0, cut) : title;
  const rest = cut > 0 ? title.slice(cut) : '';
  const subtitle = typeof c.subtitle === 'string' ? c.subtitle.trim() : DEFAULT_STORIES.subtitle;
  return (
    <section className="w-full py-10 sm:py-14 px-3 sm:px-4">
      <div className="text-center mb-8 sm:mb-10">
        <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-grayscale-1300">
          <span className="text-primary-600">{accent}</span>{rest}
        </h2>
        {subtitle && (
          <p className="mt-2 text-sm sm:text-base text-grayscale-1100">
            {subtitle}
          </p>
        )}
      </div>
      <div className="max-w-4xl mx-auto divide-y divide-grayscale-100 sm:divide-y-0 sm:space-y-4">
        {rows.map((r, i) => (
          <div
            key={i}
            // Mobile: stack the three lines with breathing room and a hairline
            // divider between pairs so consecutive rows don't visually merge.
            // Desktop: 3-col grid with the verb sandwiched between two leaders.
            className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-center gap-1.5 sm:gap-4 text-center py-4 sm:py-0"
          >
            <span className="text-sm sm:text-base text-grayscale-1300 font-medium sm:font-normal sm:text-right">{r.from}</span>
            {/* Dotted connector with a filled dot at each end, matching the
                design mock. The lines are `border-t border-dotted` (top edge
                dotted) sandwiched between two `rounded-full` dots. */}
            <span className="hidden sm:flex items-center gap-2 px-3 py-1 text-primary-600 italic text-xs sm:text-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0"></span>
              <span className="w-10 sm:w-16 border-t-2 border-dotted border-primary-400"></span>
              <span className="whitespace-nowrap">{r.verb}</span>
              <span className="w-10 sm:w-16 border-t-2 border-dotted border-primary-400"></span>
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0"></span>
            </span>
            <span className="sm:hidden text-xs italic text-primary-600">{r.verb}</span>
            <span className="text-sm sm:text-base text-grayscale-1300 sm:text-left">{r.to}</span>
          </div>
        ))}
      </div>
    </section>
  );
};


// ---------------------------------------------------------------------------
// 7. Roadmap — three pillars
// ---------------------------------------------------------------------------

interface RoadmapSectionProps {
  /** Used for COSMOS Search "Sign Up to Explore" — opens the full Clerk signup flow. */
  onSignUp: (source?: string) => void;
  /** Used for OCEAN Search / OCEAN Blue early-access flows — opens the lightweight modal. */
  onEarlyAccess: (source: EarlyAccessSource) => void;
}

interface RoadmapCard {
  audience: string;
  title: string;
  blurb: string[];
  cta: string;
  /** When set, the CTA opens the early-access modal with this source. */
  earlyAccessSource?: EarlyAccessSource;
  /** Optional source string for the full signup flow (kept for analytics parity). */
  signupSource?: string;
}

export const RoadmapSection: React.FC<RoadmapSectionProps> = ({ onSignUp, onEarlyAccess }) => {
  const cards: RoadmapCard[] = [
    {
      audience: 'For Industry & Investors',
      title: 'COSMOS Search',
      blurb: ['Tech offers for licensing', '+ directly contact TTO'],
      cta: 'Sign Up to Explore',
    },
    {
      audience: 'For Researchers & TTOs',
      title: 'OCEAN Search',
      blurb: ['Real, actionable problems', '+ benchmarked solutions'],
      cta: 'Sign Up for Early Access',
      earlyAccessSource: 'ocean_search',
    },
    {
      audience: 'For Investors & Industry',
      title: 'OCEAN Blue',
      blurb: ['Strategic intelligence', '+ white space opportunities'],
      cta: 'Sign Up for Early Access',
      earlyAccessSource: 'ocean_blue',
    },
  ];
  return (
    <section className="w-full py-10 sm:py-14 px-3 sm:px-4">
      <div className="text-center mb-8 sm:mb-10">
        <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-grayscale-1300">Our Roadmap</h2>
        <p className="mt-2 text-sm sm:text-base text-grayscale-1100">
          Simplifying and speeding up tech transfer for all stakeholders
        </p>
      </div>
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        {cards.map(c => (
          <div
            key={c.title}
            // Fieldset-style card: the audience label sits over the top
            // border with a white background that visually "cuts" the
            // border line behind it. `pt-8` reserves room so the title
            // doesn't bump into the floating label.
            className="relative rounded-2xl border border-grayscale-300 bg-white pt-8 px-5 pb-5 sm:pt-10 sm:px-6 sm:pb-6 flex flex-col items-center text-center"
          >
            <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 sm:-top-3 bg-white px-3 text-sm sm:text-base text-grayscale-1100 whitespace-nowrap">
              {c.audience}
            </span>
            <h3 className="text-lg sm:text-xl font-bold text-secondary-og-600 mb-3">{c.title}</h3>
            <div className="flex-1 text-sm text-grayscale-1200 mb-5">
              {c.blurb.map(line => <div key={line}>{line}</div>)}
            </div>
            <button
              onClick={() => {
                trackClick(`roadmap_${c.title}`, { source: c.earlyAccessSource || c.signupSource });
                if (c.earlyAccessSource) {
                  onEarlyAccess(c.earlyAccessSource);
                } else {
                  onSignUp(c.signupSource);
                }
              }}
              className="w-full px-4 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
            >{c.cta}</button>
          </div>
        ))}
      </div>
    </section>
  );
};


// ---------------------------------------------------------------------------
// 8. Testimonials
// ---------------------------------------------------------------------------

export interface Testimonial {
  quote: string;
  rating: number;
  name?: string;
  designation?: string;
  organisation?: string;
  is_anonymous?: boolean;
}

// Testimonials are sourced from About Us (components/AboutUsPage.tsx). Both are
// real, anonymised quotes; we keep the speakers anonymous on the public page
// and use only their role + organisation for context.
const DEFAULT_TESTIMONIALS: Testimonial[] = [
  {
    quote:
      'Your platform is a game-changer for those of us passionate about biomaterials, sustainability, and technology. The way you bridge academic research with real-world applications is truly inspiring and essential for driving meaningful innovation.',
    rating: 5,
    is_anonymous: true,
    designation: 'Promoter of certified compostable products manufacturer',
  },
  {
    quote:
      'Your structured model of facilitating technology access from top global universities aligns seamlessly with our mission to foster East-West collaboration, particularly by connecting innovative solutions within our networks to international markets and vice versa.',
    rating: 5,
    is_anonymous: true,
    designation: 'Director at €170mn+ tech transfer organisation',
  },
];

export const Testimonials: React.FC<{ items?: Testimonial[] }> = ({ items }) => {
  const list = items && items.length > 0 ? items : DEFAULT_TESTIMONIALS;
  return (
    <section className="w-full py-10 sm:py-14 px-3 sm:px-4">
      <div className="text-center mb-8 sm:mb-10">
        <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-grayscale-1300">
          Sneak peek of the <span className="text-primary-600">❤</span> we have received
        </h2>
        <p className="mt-2 text-sm sm:text-base text-grayscale-1100">
          Your feedback is gold dust, fuelling our escape velocity
        </p>
      </div>
      <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {list.map((t, i) => (
          <article key={i} className="rounded-2xl border border-primary-200 bg-white p-5 sm:p-7 shadow-sm">
            <div className="flex gap-0.5 mb-3 text-primary-500" aria-label={`${t.rating} out of 5 stars`}>
              {Array.from({ length: 5 }).map((_, j) => (
                <span key={j} className={j < t.rating ? '' : 'opacity-25'}>★</span>
              ))}
            </div>
            <div className="flex items-start gap-3">
              <div className="text-3xl sm:text-4xl text-primary-600 leading-none -mt-1">"</div>
              <p className="text-sm sm:text-base text-grayscale-1200 italic leading-relaxed">
                {t.quote}
              </p>
            </div>
            <div className="mt-4 pl-7">
              <div className="text-xs sm:text-sm font-medium text-grayscale-1300">
                {t.is_anonymous ? (t.designation || 'Anonymous') : (t.name || 'Anonymous')}
              </div>
              {!t.is_anonymous && (
                <div className="text-[11px] sm:text-xs text-grayscale-1100">
                  {[t.designation, t.organisation].filter(Boolean).join(' · ')}
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};


// ---------------------------------------------------------------------------
// 9. Discover Your Next — rotating word + List Your IP modal
// ---------------------------------------------------------------------------

const ROTATING_WORDS = [
  'GROWTH ENGINE',
  'PROCESS IMPROVEMENT',
  'BREAKTHROUGH',
  'COSTS SAVINGS',
  'DIVERSIFICATION',
  'COMPETITIVE MOAT',
];

// Longest word in `ROTATING_WORDS` — used as the invisible sizer in the
// DiscoverNext chip so the chip width never changes per rotation.
// Computed at module load so adding a longer word later just works.
const LONGEST_ROTATING_WORD = ROTATING_WORDS.reduce(
  (longest, w) => (w.length > longest.length ? w : longest),
  '',
);

interface DiscoverNextProps {
  onSignUp: () => void;
  onListYourIP: () => void;
}

export const DiscoverNext: React.FC<DiscoverNextProps> = ({ onSignUp, onListYourIP }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setIdx(i => (i + 1) % ROTATING_WORDS.length), 1500);
    return () => clearInterval(id);
  }, []);
  return (
    <section className="w-full py-10 sm:py-14 px-3 sm:px-4 text-center">
      <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-grayscale-1300 leading-tight flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3 flex-wrap">
        <span>Discover your next</span>
        {/* Invisible-sizer technique: a hidden copy of the longest word
            ("PROCESS IMPROVEMENT") sets the chip's width once, and the
            active rotating word is rendered absolutely on top. This makes
            the chip width truly constant — so the surrounding "Discover
            your next" / "with licensable IP" never shifts when the word
            changes, even when "PROCESS IMPROVEMENT" is on screen. Font is
            one notch smaller than the heading so the longest word fits
            without making the chip dominate the row. */}
        <span className="relative inline-flex items-center justify-center px-3 sm:px-4 py-1 rounded-full bg-secondary-100 border border-secondary-400 text-secondary-og-600 text-xs sm:text-sm lg:text-base tracking-wide">
          <span aria-hidden="true" className="invisible whitespace-nowrap">
            {LONGEST_ROTATING_WORD}
          </span>
          <span
            key={idx}
            className="absolute inset-0 flex items-center justify-center cosmos-fade whitespace-nowrap"
          >
            {ROTATING_WORDS[idx]}
          </span>
        </span>
        <span>with licensable IP</span>
      </h2>
      <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
        <button
          onClick={() => { trackClick('discover_signup'); onSignUp(); }}
          className="w-full sm:w-auto px-6 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white font-medium text-sm transition-colors"
        >Sign Up to Explore</button>
        <button
          onClick={() => { trackClick('discover_list_your_ip'); onListYourIP(); }}
          className="w-full sm:w-auto px-6 py-2.5 rounded-full border border-primary-600 text-primary-600 hover:bg-primary-50 font-medium text-sm transition-colors"
        >List Your IP</button>
      </div>
      <style>{`
        @keyframes cosmosFade {
          0%   { opacity: 0; transform: translateY(4px); }
          15%  { opacity: 1; transform: translateY(0); }
          85%  { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        .cosmos-fade { animation: cosmosFade 1.5s ease-in-out; }
      `}</style>
    </section>
  );
};


// ---- List Your IP modal ----------------------------------------------------

interface ListYourIPModalProps {
  open: boolean;
  onClose: () => void;
}

export const ListYourIPModal: React.FC<ListYourIPModalProps> = ({ open, onClose }) => {
  const [form, setForm] = useState({ full_name: '', email: '', designation: '', university_name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      // Reset on close so the next open is fresh.
      setTimeout(() => { setForm({ full_name: '', email: '', designation: '', university_name: '' }); setDone(false); setError(''); }, 250);
    }
  }, [open]);

  if (!open) return null;

  const valid = form.full_name && form.email && form.designation && form.university_name && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    trackClick('list_your_ip_submit', { email: form.email });
    try {
      const res = await (apiService as any).submitUniversityLead?.({ ...form, form_source: 'list_your_ip_homepage' });
      if (res?.success) {
        setDone(true);
        trackClick('list_your_ip_success', { email: form.email });
      } else {
        const msg = res?.error || 'Submission failed. Please try again.';
        setError(msg);
        trackClick('list_your_ip_error', { error: msg });
      }
    } catch (e: any) {
      const msg = e?.message || 'Submission failed. Please try again.';
      setError(msg);
      trackClick('list_your_ip_error', { error: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-5 sm:p-6 shadow-2xl">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-grayscale-100 flex items-center justify-center text-grayscale-1100 text-xl leading-none"
        >×</button>

        {done ? (
          <div className="text-center py-6">
            <div className="text-3xl mb-2">✓</div>
            <h3 className="text-lg font-bold text-grayscale-1300 mb-2">Thanks — we'll be in touch.</h3>
            <p className="text-sm text-grayscale-1100">
              Our team will reach out shortly to learn more about your IP.
            </p>
            <button
              onClick={onClose}
              className="mt-4 px-5 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-sm"
            >Close</button>
          </div>
        ) : (
          <>
            <h3 className="text-lg sm:text-xl font-bold text-grayscale-1300 mb-1">List Your IP</h3>
            <p className="text-sm text-grayscale-1100 mb-4">
              Tell us a little about yourself and our team will reach out to discuss listing.
            </p>
            <div className="space-y-3">
              <Input label="Full name" value={form.full_name} onChange={v => setForm(f => ({ ...f, full_name: v }))} />
              <Input label="Email address" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} />
              <Input label="Designation" value={form.designation} onChange={v => setForm(f => ({ ...f, designation: v }))} placeholder="e.g. Tech Transfer Officer" />
              <Input label="University name" value={form.university_name} onChange={v => setForm(f => ({ ...f, university_name: v }))} />
              {error && <div className="text-xs text-red-600">{error}</div>}
              <button
                onClick={submit}
                disabled={!valid || submitting}
                className="w-full mt-2 px-5 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 disabled:bg-grayscale-300 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors"
              >{submitting ? 'Submitting…' : 'Submit'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};


// ---- Early Access modal ----------------------------------------------------
//
// Used by the Roadmap "Sign Up for Early Access" CTAs (OCEAN Search, OCEAN Blue).
// Same fields as List Your IP per product decision; the `source` prop tags
// the row so we can track which product the user signed up for.

export type EarlyAccessSource = 'ocean_blue' | 'ocean_search' | 'cosmos_search';

interface EarlyAccessModalProps {
  open: boolean;
  onClose: () => void;
  source: EarlyAccessSource | null;
  /** Display copy override per source. Defaults derived from `source`. */
  title?: string;
  subtitle?: string;
}

const SOURCE_COPY: Record<EarlyAccessSource, { title: string; subtitle: string }> = {
  ocean_blue: {
    title: 'Sign up for OCEAN Blue early access',
    subtitle: 'Strategic intelligence + white space opportunities. We’ll email you the moment it’s ready.',
  },
  ocean_search: {
    title: 'Sign up for OCEAN Search early access',
    subtitle: 'Real, actionable problems + benchmarked solutions. We’ll email you when it’s live.',
  },
  cosmos_search: {
    title: 'Sign up to explore COSMOS Search',
    subtitle: 'Tech offers for licensing + direct contact with TTOs. Tell us a little about yourself.',
  },
};

export const EarlyAccessModal: React.FC<EarlyAccessModalProps> = ({ open, onClose, source, title, subtitle }) => {
  const [form, setForm] = useState({ full_name: '', email: '', designation: '', university_name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setForm({ full_name: '', email: '', designation: '', university_name: '' });
        setDone(false); setError('');
      }, 250);
    }
  }, [open]);

  if (!open || !source) return null;

  const copy = { ...SOURCE_COPY[source], ...(title ? { title } : {}), ...(subtitle ? { subtitle } : {}) };

  const valid = form.full_name && form.email && form.designation && form.university_name && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    setError('');
    trackClick('early_access_submit', { source, email: form.email });
    try {
      const res = await (apiService as any).submitEarlyAccess?.({ ...form, source, form_source: `early_access_${source}` });
      if (res?.success) {
        setDone(true);
        trackClick('early_access_success', { source, email: form.email });
      } else {
        const msg = res?.error || 'Submission failed. Please try again.';
        setError(msg);
        trackClick('early_access_error', { source, error: msg });
      }
    } catch (e: any) {
      const msg = e?.message || 'Submission failed. Please try again.';
      setError(msg);
      trackClick('early_access_error', { source, error: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-md p-5 sm:p-6 shadow-2xl">
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-grayscale-100 flex items-center justify-center text-grayscale-1100 text-xl leading-none"
        >×</button>

        {done ? (
          <div className="text-center py-6">
            <div className="text-3xl mb-2">✓</div>
            <h3 className="text-lg font-bold text-grayscale-1300 mb-2">You're on the list.</h3>
            <p className="text-sm text-grayscale-1100">
              We'll email you the moment early access opens up.
            </p>
            <button
              onClick={onClose}
              className="mt-4 px-5 py-2 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-sm"
            >Close</button>
          </div>
        ) : (
          <>
            <h3 className="text-lg sm:text-xl font-bold text-grayscale-1300 mb-1">{copy.title}</h3>
            <p className="text-sm text-grayscale-1100 mb-4">{copy.subtitle}</p>
            <div className="space-y-3">
              <Input label="Full name" value={form.full_name} onChange={v => setForm(f => ({ ...f, full_name: v }))} />
              <Input label="Email address" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} />
              <Input label="Designation" value={form.designation} onChange={v => setForm(f => ({ ...f, designation: v }))} placeholder="e.g. Director, Tech Transfer / VP, Strategy" />
              <Input label="University / Organisation" value={form.university_name} onChange={v => setForm(f => ({ ...f, university_name: v }))} />
              {error && <div className="text-xs text-red-600">{error}</div>}
              <button
                onClick={submit}
                disabled={!valid || submitting}
                className="w-full mt-2 px-5 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 disabled:bg-grayscale-300 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors"
              >{submitting ? 'Submitting…' : 'Submit'}</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Input: React.FC<{ label: string; value: string; onChange: (v: string) => void; type?: string; placeholder?: string }> = ({ label, value, onChange, type = 'text', placeholder }) => (
  <label className="block">
    <span className="block text-xs text-grayscale-1100 mb-1">{label}</span>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
    />
  </label>
);


// ---------------------------------------------------------------------------
// 10. Footer — newsletter + columns
// ---------------------------------------------------------------------------

interface NewFooterProps {
  onShowAboutUs?: () => void;
  onShowPrivacy?: () => void;
  onShowTerms?: () => void;
  /**
   * Optional handler for the R&D Symposium footer link. When omitted the
   * link is hidden — pass it on pages that should expose the symposium
   * recap (homepage, the symposium page itself, etc.).
   */
  onShowRdSymposium?: () => void;
  /**
   * Optional handler for the White Paper footer link. When provided, the
   * link is rendered in the right-hand column and clicking it opens the
   * shared <WhitePaperModal/>. When omitted the entry is hidden.
   */
  onShowWhitePaper?: () => void;
  /**
   * Optional handler for the Universities footer link. On the homepage we
   * wire this to smooth-scroll to the UniversityTicker. On other pages we
   * hard-nav to "/#universities" so the homepage scrolls into view on load.
   */
  onShowUniversities?: () => void;
  /**
   * Optional handler for the COMET footer link (the /comet Phase 1 landing
   * page). Omitted only where a COMET link would be redundant.
   */
  onShowComet?: () => void;
  /**
   * The IP Intel Report link is on every footer (site-wide, like the nav
   * entry); by default it navigates to /ip-intel-report. The report pages
   * pass their own handler (scroll to top) and `ipIntelActive` to mark it.
   */
  onShowIpIntel?: () => void;
  ipIntelActive?: boolean;
}

const LINKEDIN_URL = 'https://www.linkedin.com/company/university-tech';

// wa.me needs the full international number with no plus, spaces or dashes;
// the bare 10-digit form silently resolves to whatever country the viewer's
// WhatsApp is registered in.
const WHATSAPP_NUMBER = '918108884410';
const WHATSAPP_DISPLAY = '+91 81088 84410';
const WHATSAPP_URL =
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    "Hi UniversityTech, I'd like to know more.")}`;

const WhatsAppGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className || 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884a9.82 9.82 0 0 1 6.988 2.896 9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488" />
  </svg>
);

/**
 * Floating WhatsApp button, shown on the public pages only.
 *
 * Rendered from NewFooter rather than wired into each page: the footer is on
 * every public page and on none of the signed-in app, so it already describes
 * exactly the surface this belongs on, and there is one place to change it.
 *
 * z-40 keeps it under the modals (z-[60]) — a chat button floating over an
 * open dialog would sit on top of the thing it interrupts.
 */
export const WhatsAppFab: React.FC = () => (
  <a
    href={WHATSAPP_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackClick('whatsapp_fab')}
    aria-label={`Chat with us on WhatsApp at ${WHATSAPP_DISPLAY}`}
    title={`WhatsApp ${WHATSAPP_DISPLAY}`}
    className="fixed z-40 bottom-5 right-5 sm:bottom-6 sm:right-6 flex items-center
               justify-center w-14 h-14 rounded-full bg-[#25D366] text-white
               shadow-lg shadow-black/20 hover:bg-[#1EBE57] hover:scale-105
               focus:outline-none focus-visible:ring-4 focus-visible:ring-[#25D366]/40
               transition-all duration-200"
  >
    <WhatsAppGlyph className="w-7 h-7" />
  </a>
);

const FooterLinkedInIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className || 'w-5 h-5'} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

export const NewFooter: React.FC<NewFooterProps> = ({ onShowAboutUs, onShowPrivacy, onShowTerms, onShowRdSymposium, onShowWhitePaper, onShowUniversities, onShowComet, onShowIpIntel, ipIntelActive }) => {
  const showIpIntel = onShowIpIntel || (() => {
    trackClick('ipintel_click_nav_link', { where: 'footer', from: window.location.pathname });
    window.location.href = '/ip-intel-report';
  });
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handle = async () => {
    trackClick('footer_newsletter_subscribe', { email });
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setState('error'); setErrorMsg('Please enter a valid email.');
      return;
    }
    setState('submitting'); setErrorMsg('');
    try {
      const res = await (apiService as any).submitNewsletterSignup?.(email, 'homepage_footer_newsletter');
      if (res?.success) {
        setState('success'); setEmail('');
        trackClick('footer_newsletter_subscribe_success', { already_subscribed: !!res?.already_subscribed });
      } else {
        setState('error');
        setErrorMsg(res?.error || 'Something went wrong, please try again.');
        trackClick('footer_newsletter_subscribe_error', { error: res?.error });
      }
    } catch (e: any) {
      setState('error'); setErrorMsg(e?.message || 'Something went wrong, please try again.');
      trackClick('footer_newsletter_subscribe_error', { error: e?.message });
    }
  };

  return (
    <footer className="w-full border-t border-grayscale-200 bg-white mt-10 sm:mt-14 pt-10 sm:pt-12 pb-6">
      {/* 4-col grid on desktop: newsletter (2 cols) + two link columns (1
          col each). Below md, everything stacks. The right-hand link column
          (R&D Symposium / White Paper) only renders the entries whose
          handlers are wired by the parent page, so we don't ship dead links.
          What's New / Cosmos Club / Universities are intentionally hidden
          until those pages exist. */}
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-4 gap-8">
        {/* Newsletter — spans 2 of the 4 columns */}
        <div className="md:col-span-2">
          <h3 className="text-lg font-bold text-grayscale-1300">Sign up to The Cosmos</h3>
          <p className="mt-2 text-xs sm:text-sm text-grayscale-1100">
            Keep up with global licensing news, upcoming features and events. Monthly, no spam, no 3rd party.
          </p>
          <div className="mt-4 flex gap-2 max-w-md">
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); if (state !== 'idle') setState('idle'); }}
              onKeyDown={e => { if (e.key === 'Enter') handle(); }}
              placeholder="Please enter your e-mail address"
              className="flex-1 px-3 py-2 border border-grayscale-300 rounded-lg text-sm focus:outline-none focus:border-primary-500"
            />
            <button
              onClick={handle}
              disabled={state === 'submitting'}
              className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 disabled:bg-grayscale-300 text-white text-sm font-medium"
            >{state === 'submitting' ? '…' : 'Subscribe'}</button>
          </div>
          {state === 'success' && <p className="mt-2 text-xs text-green-600">You're subscribed!</p>}
          {state === 'error' && <p className="mt-2 text-xs text-red-600">{errorMsg}</p>}
        </div>

        {/* Column 1 — primary nav links (top group). Order requested by
            product: About Us → Universities → COMET → R&D Symposium →
            White Paper. Each conditional link only renders when the parent
            wires its handler so we never ship dead footer links. */}
        <div>
          <FooterColumn title="" links={[
            { label: 'About Us', href: '#', onClick: onShowAboutUs },
            { label: 'IP Intel Report', href: '/ip-intel-report', onClick: showIpIntel, active: ipIntelActive },
            ...(onShowUniversities ? [{ label: 'Universities', href: '#', onClick: onShowUniversities }] : []),
            ...(onShowComet ? [{ label: 'COMET', href: '#', onClick: onShowComet }] : []),
            ...(onShowRdSymposium ? [{ label: 'R&D Symposium', href: '#', onClick: onShowRdSymposium }] : []),
            ...(onShowWhitePaper ? [{ label: 'White Paper', href: '#', onClick: onShowWhitePaper }] : []),
          ]} />
        </div>

        {/* Column 2 — legal links (separated visual group) + LinkedIn */}
        <div className="flex flex-col gap-4">
          <FooterColumn title="" links={[
            { label: 'Terms of Service', href: '#', onClick: onShowTerms },
            { label: 'Privacy Policy', href: '#', onClick: onShowPrivacy },
          ]} />
          <a
            href={LINKEDIN_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackClick('footer_linkedin')}
            className="inline-flex items-center gap-2 text-secondary-og-600 hover:text-secondary-500 transition-colors text-xs sm:text-sm w-fit"
            aria-label="Follow us on LinkedIn"
          >
            <FooterLinkedInIcon className="w-5 h-5" />
            <span>Follow us on LinkedIn</span>
          </a>
        </div>
      </div>
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 pt-6 border-t border-grayscale-100 text-center">
        <p className="text-[11px] sm:text-xs text-grayscale-1100">
          All rights reserved. Paragise Private Limited 2024 - 2026.
        </p>
      </div>
      <WhatsAppFab />
    </footer>
  );
};

const FooterColumn: React.FC<{ title: string; links: { label: string; href: string; onClick?: () => void; active?: boolean }[] }> = ({ title, links }) => (
  <div>
    {title && <h4 className="text-sm font-semibold text-grayscale-1300 mb-3">{title}</h4>}
    <ul className="space-y-2">
      {links.map(l => (
        <li key={l.label}>
          <a
            href={l.href}
            onClick={e => { if (l.onClick) { e.preventDefault(); l.onClick(); } trackClick(`footer_${l.label}`); }}
            className={l.active
              ? 'text-xs sm:text-sm font-bold text-[#725737]'
              : 'text-xs sm:text-sm text-grayscale-1100 hover:text-grayscale-1300 transition-colors'}
            {...(l.active ? { 'aria-current': 'page' as const } : {})}
          >{l.label}</a>
        </li>
      ))}
    </ul>
  </div>
);
